import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import React from 'react';
import { useTranslate } from 'hooks/useTranslate';

const ReviewItem = styled('div')(({ theme }) => ({
  padding: 24,
  backgroundColor: 'rgba(238, 237, 242, 1)',
  zIndex: 10,
  position: 'relative',
  borderRadius: 18,
  display: 'flex',
  gap: 20,
  minHeight: 180,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    minHeight: 180
  }
}));

const ReviewName = styled(Typography)(() => ({
  fontWeight: 500,
  fontSize: 16,
  marginBottom: 12,
  lineHeight: '24px',
  color: 'rgba(10, 10, 10, 1)'
}));

const ReviewText = styled(Typography, { shouldForwardProp: (prop) => prop !== 'expanded' })(
  ({ expanded }) => ({
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '20px',
    color: 'rgba(10, 10, 10, 1)',
    ...(expanded
      ? {}
      : {
          display: '-webkit-box',
          WebkitLineClamp: 7,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        })
  })
);

const LoadMoreStyled = styled(Button)(({ theme }) => ({
  display: 'flex',
  boxShadow: 'none',
  fontSize: 16,
  fontWeight: 500,
  color: 'rgba(68, 68, 68, 1)',
  cursor: 'pointer',
  margin: '0 auto',
  marginTop: 10,
  '&:hover': {
    backgroundColor: theme.palette.primary.salad,
    color: 'rgba(10, 10, 10, 1)'
  }
}));

const StarIcon = ({
  theme,
  filled = false,
  size = 24,
  colorFilled = theme.palette.secondary.main,
  colorEmpty = theme.palette.grey[200]
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false">
    <path
      d="M12 2.75l2.938 5.953 6.572.956-4.755 4.636 1.123 6.547L12 17.957 6.122 20.842l1.123-6.547L2.49 9.66l6.572-.956L12 2.75z"
      fill={filled ? colorFilled : colorEmpty}
    />
  </svg>
);

const RatingBox = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  padding: 12,
  paddingTop: 16,
  alignSelf: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '100%',
    padding: 0,
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    paddingTop: 8,
    paddingBottom: 8
  }
}));

const RatingValue = styled(Typography)(() => ({
  fontSize: 30,
  fontWeight: 500,
  textAlign: 'center',
  color: 'rgba(10, 10, 10, 1)'
}));

const StarsRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  gap: 4,
  marginTop: 6,
  marginBottom: 10
}));

const Divider = styled('div')(({ theme }) => ({
  height: 1,
  backgroundColor: theme.palette.primary.main,
  margin: '8px 0 12px 0',
  [theme.breakpoints.down('sm')]: {
    width: 1,
    height: 'auto',
    alignSelf: 'stretch',
    margin: 0
  }
}));

const AspectItem = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '6px 0'
}));

const AspectLabel = styled(Typography)(() => ({
  fontSize: 15,
  lineHeight: '20px',
  color: 'rgba(10, 10, 10, 1)'
}));

const AspectValue = styled(Typography)(() => ({
  fontSize: 15,
  lineHeight: '20px',
  fontWeight: 500,
  color: 'rgba(10, 10, 10, 1)'
}));

const ReviewHeader = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  marginBottom: 12,
  width: '100%',
  justifyContent: 'space-between'
}));

const InlineStars = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4
}));

const Review = ({ name, text, idx, rating = 0, aspects, markArray }) => {
  const tLocal = useTranslate('Home');
  const tQuestions = useTranslate('ReviewModal');
  const [expanded, setExpanded] = React.useState(false);
  const [needsToggle, setNeedsToggle] = React.useState(false);
  const containerRef = React.useRef(null);
  const measureRef = React.useRef(null);

  const aspectItems = React.useMemo(() => {
    if (Array.isArray(aspects) && aspects.length) return aspects;
    if (Array.isArray(markArray) && markArray.length) {
      const labels = [
        tQuestions('QTeachersShort'),
        tQuestions('QPracticalSkillsShort'),
        tQuestions('QStudentsInteractionShort'),
        tQuestions('QScheduleConvenienceShort'),
        tQuestions('QCostVsResultShort')
      ];
      return markArray.map((obj, idx) => {
        const value = typeof obj === 'object' && obj ? Object.values(obj)[0] : null;
        return {
          label: labels[idx] || `Parameter ${idx + 1}`,
          value: value ?? '-'
        };
      });
    }
    return [];
  }, [aspects, markArray, tQuestions]);

  const hasAspects = aspectItems.length > 0;

  React.useEffect(() => {
    const recompute = () => {
      const container = containerRef.current;
      const measure = measureRef.current;
      if (!container || !measure) return;
      const width = container.clientWidth;
      measure.style.width = `${width}px`;
      const lineHeight = 20;
      const lines = Math.ceil(measure.scrollHeight / lineHeight);
      setNeedsToggle(lines > 7);
    };

    recompute();

    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [text]);

  const textId = `review-text-${idx}`;

  const RenderName = () => {
    const splitName = name.trim().split(' ');

    if (splitName.length === 2) {
      return (
        <ReviewName style={{ marginBottom: 0 }}>
          {splitName[0]} {splitName[1][0]}.
        </ReviewName>
      );
    }

    return <ReviewName style={{ marginBottom: 0 }}>{name}</ReviewName>;
  };

  if (hasAspects) {
    return (
      <ReviewItem ref={containerRef}>
        <RatingBox>
          <div>
            <RatingValue component="div">{Number(rating).toFixed(1)}</RatingValue>
            <StarsRow role="img" aria-label={tLocal('ReviewRating', { rating })}>
              {Array.from({ length: 5 }).map((_, i) => (
                <StarIcon key={i} filled={i < Math.round(rating)} />
              ))}
            </StarsRow>
          </div>
          <Divider />
          <div>
            {aspectItems.map((a, i) => (
              <AspectItem key={`${a.label}-${i}`}>
                <AspectLabel>{a.label}</AspectLabel>
                <AspectValue>{a.value}</AspectValue>
              </AspectItem>
            ))}
          </div>
        </RatingBox>
        <div>
          <ReviewName>{name}</ReviewName>
          <ReviewText expanded={expanded} id={textId}>
            {text}
          </ReviewText>
          <Typography
            component="div"
            ref={measureRef}
            sx={{
              position: 'absolute',
              visibility: 'hidden',
              pointerEvents: 'none',
              left: 0,
              top: 0,
              zIndex: -1,
              whiteSpace: 'normal',
              fontWeight: 400,
              fontSize: 16,
              lineHeight: '20px'
            }}>
            {text}
          </Typography>
          {needsToggle && (
            <LoadMoreStyled
              aria-label={expanded ? tLocal('ShowLess') : tLocal('ShowMore')}
              aria-expanded={expanded}
              aria-controls={textId}
              onClick={() => setExpanded((v) => !v)}>
              {expanded ? tLocal('ShowLess') : tLocal('ShowMore')}
            </LoadMoreStyled>
          )}
        </div>
      </ReviewItem>
    );
  }

  return (
    <ReviewItem ref={containerRef}>
      <div style={{ width: '100%' }}>
        <ReviewHeader>
          <RenderName />
          <InlineStars role="img" aria-label={tLocal('ReviewRating', { rating })}>
            {Array.from({ length: 5 }).map((_, i) => (
              <StarIcon key={i} size={18} filled={i < Math.round(rating)} />
            ))}
          </InlineStars>
        </ReviewHeader>
        <ReviewText expanded={expanded} id={textId}>
          {text}
        </ReviewText>
        <Typography
          component="div"
          ref={measureRef}
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            left: 0,
            top: 0,
            zIndex: -1,
            whiteSpace: 'normal',
            fontWeight: 400,
            fontSize: 16,
            lineHeight: '20px'
          }}>
          {text}
        </Typography>
        {needsToggle && (
          <LoadMoreStyled
            aria-label={expanded ? tLocal('ShowLess') : tLocal('ShowMore')}
            aria-expanded={expanded}
            aria-controls={textId}
            onClick={() => setExpanded((v) => !v)}>
            {expanded ? tLocal('ShowLess') : tLocal('ShowMore')}
          </LoadMoreStyled>
        )}
      </div>
    </ReviewItem>
  );
};

export default Review;
